// The Claude support of one signed-in account in the app (framework-free, AD-34; React subscribes like to the other
// stores). Everything lives in the cloud (Dominik, 19.10.2026: key per account, stored by Brewcore; the cloud is the
// source of truth): the key (only "gespeichert · …abcd" comes back), model and effort, the conversation per shot, the
// grinder, whether the app asks by itself after a sour or bitter rating and the dismissed setup line (Dominik
// 22.10.2026). The API asks Anthropic; without a key or a connection there is no suggestion, never an invented one.
import type {
  ClaudeAccountResponse,
  ClaudeBeanResponse,
  ClaudeGrinderResponse,
  ClaudeSettingsRequest,
  ClaudeThreadResponse,
  Lang,
} from '@brewcore/app-contract';
import { ClaudeApi } from './api.js';
import type { AiChange } from './changes.js';
import { browserStorage, forgetClaudeOnDevice, legacyKey, type KeyValueStorage } from './storage.js';

export interface AssistantView {
  /** the account's Claude settings came from the API */
  loaded: boolean;
  account: ClaudeAccountResponse | null;
  /** a key is being checked and saved, deleted, or a setting saved */
  saving: boolean;
  /** why the key was not taken, or why loading failed (a reason code) */
  keyError: string | null;
  /** a key the first version left in this browser: offered once for the account */
  legacy: { last4: string } | null;
  /** changes with every update (React re-renders) */
  rev: number;
}

export interface ShotAiView {
  thread: ClaudeThreadResponse | null;
  loaded: boolean;
  /** Claude is answering: a suggestion or a chat message */
  busy: 'suggest' | 'chat' | null;
  /** the last request failed (a reason code); never an invented answer */
  error: string | null;
  /** which request failed: the card shows the suggestion's and the loading's, the chat sheet the chat's */
  errorFor: 'suggest' | 'chat' | 'load' | null;
  /** a chat message that could not be sent: back into the field */
  unsent: string | null;
  /** the chat message on its way (shown until Claude answered) */
  pending: string | null;
  /** when the suggestion now on its way was asked for (ms): after about 20 s the card says it takes long */
  since: number | null;
  /** "Ausblenden" while Claude took long: the card after the shot stays away, the shot's page shows the answer */
  hidden: boolean;
  /** what an adopted proposal changed (old values) in this session: "Rückgängig" sets them back */
  undo: { index: number; changes: AiChange[] } | null;
}

export interface GrinderView {
  data: ClaudeGrinderResponse | null;
  busy: boolean;
  error: string | null;
}

/** What Claude found out about one bean (board "Eine Bohne"). */
export interface BeanLookupView {
  data: ClaudeBeanResponse | null;
  busy: boolean;
  error: string | null;
}

export interface AssistantOptions {
  api?: ClaudeApi;
  /** where the first version kept its key (default: this browser's localStorage) */
  storage?: KeyValueStorage | null;
  /** the clock (tests) */
  now?: () => number;
}

interface ShotState {
  thread: ClaudeThreadResponse | null;
  loaded: boolean;
  loading: boolean;
  busy: 'suggest' | 'chat' | null;
  error: string | null;
  errorFor: 'suggest' | 'chat' | 'load' | null;
  unsent: string | null;
  pending: string | null;
  since: number | null;
  hidden: boolean;
  undo: { index: number; changes: AiChange[] } | null;
  /** the last suggestion asked for: asked again once a key error is fixed */
  asked: { machine: string; shot: string; lang: Lang; at: number } | null;
}

/**
 * Failures that Einstellungen → Claude fixes (a key that is missing or refused, a model the key cannot use): the card
 * leads there, and a new key (or model) takes them back.
 */
export const KEY_ERRORS: ReadonlySet<string> = new Set([
  'claude_no_key',
  'claude_key_invalid',
  'claude_model',
]);
const MODEL_ERRORS: ReadonlySet<string> = new Set(['claude_model']);

/** Ask Claude by itself: a key in the account, the switch on (the default), the shot rated sour (1) or bitter (3). */
export function autoAskOn(
  view: Pick<AssistantView, 'loaded' | 'account'>,
  rating: number | null | undefined,
): boolean {
  return (
    view.loaded && !!view.account?.key && view.account.autoAsk !== false && (rating === 1 || rating === 3)
  );
}

export class ClaudeAssistant {
  private readonly api: ClaudeApi;
  private readonly storage: KeyValueStorage | null;
  private readonly now: () => number;
  private readonly listeners = new Set<() => void>();
  private readonly shots = new Map<string, ShotState>();
  private readonly grinders = new Map<string, GrinderView & { loading: boolean }>();
  // per machine and bean; asked is the bean's name and roaster it was loaded for (the cloud looks a renamed bean up anew)
  private readonly beanLookups = new Map<
    string,
    BeanLookupView & { loading: boolean; asked: string | null }
  >();
  private view: AssistantView = {
    loaded: false,
    account: null,
    saving: false,
    keyError: null,
    legacy: null,
    rev: 0,
  };
  private loading: Promise<void> | null = null;

  constructor(opts: AssistantOptions = {}) {
    this.api = opts.api ?? new ClaudeApi();
    this.storage = opts.storage === undefined ? browserStorage() : opts.storage;
    this.now = opts.now ?? (() => Date.now());
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): AssistantView => this.view;

  private changed(patch: Partial<AssistantView> = {}): void {
    this.view = { ...this.view, ...patch, rev: this.view.rev + 1 };
    for (const fn of this.listeners) fn();
  }

  // ---------------------------------------------------------------------------------------------- account

  /** The account's Claude settings, once (again after a failure). */
  load(): Promise<void> {
    if (this.view.loaded) return Promise.resolve();
    this.loading ??= (async () => {
      const r = await this.api.account();
      if (!r.ok) {
        this.changed({ keyError: r.error.code });
        return;
      }
      this.changed({ loaded: true, account: r.value, keyError: null });
      this.checkLegacy();
    })().finally(() => {
      this.loading = null;
    });
    return this.loading;
  }

  /** A key of the first version in this browser: offered for an account without one, else simply removed. */
  private checkLegacy(): void {
    const key = legacyKey(this.storage);
    if (!key) return;
    if (this.view.account?.key) {
      forgetClaudeOnDevice(this.storage);
      return;
    }
    this.changed({ legacy: { last4: key.slice(-4) } });
  }

  private apply(
    r: { ok: true; value: ClaudeAccountResponse } | { ok: false; error: { code: string } },
  ): boolean {
    if (r.ok) this.changed({ account: r.value, loaded: true, keyError: null, saving: false });
    else this.changed({ keyError: r.error.code, saving: false });
    return r.ok;
  }

  /** The API checks the key once with Anthropic, then keeps it sealed in the account. */
  async saveKey(typed: string): Promise<boolean> {
    const key = typed.trim();
    if (!/^sk-ant-[A-Za-z0-9_-]{8,}$/.test(key)) {
      this.changed({ keyError: 'claude_key_format' });
      return false;
    }
    this.changed({ saving: true, keyError: null });
    const ok = this.apply(await this.api.setKey(key));
    if (ok) this.keyBack(KEY_ERRORS);
    return ok;
  }

  async deleteKey(): Promise<void> {
    this.changed({ saving: true, keyError: null });
    this.apply(await this.api.deleteKey());
  }

  /**
   * Model, effort, the switch and "Nicht jetzt" of the account. The switch and the setup line change on screen at once
   * (a switch that waits for the cloud feels broken); a refusal puts the account back as it was.
   */
  async setSettings(patch: ClaudeSettingsRequest): Promise<void> {
    const before = this.view.account;
    const quick = patch.autoAsk !== undefined || patch.setupDismissed !== undefined;
    if (quick && before) this.changed({ account: { ...before, ...patch } });
    this.changed({ saving: true });
    const r = await this.api.settings(patch);
    if (!r.ok && quick && before) this.changed({ account: before });
    if (this.apply(r) && patch.model !== undefined) this.keyBack(MODEL_ERRORS);
  }

  /** "Im Konto speichern": the key of the first version into the account, then out of this browser. */
  async saveLegacy(): Promise<boolean> {
    const key = legacyKey(this.storage);
    if (!key) {
      this.changed({ legacy: null });
      return false;
    }
    const ok = await this.saveKey(key);
    if (ok) {
      forgetClaudeOnDevice(this.storage);
      this.changed({ legacy: null });
    }
    return ok;
  }

  /** "Verwerfen": the key of the first version is removed from this browser. */
  discardLegacy(): void {
    forgetClaudeOnDevice(this.storage);
    this.changed({ legacy: null, keyError: null });
  }

  // ---------------------------------------------------------------------------------------------- per shot

  private state(machine: string, shot: string): ShotState {
    const k = `${machine}/${shot}`;
    let s = this.shots.get(k);
    if (!s) {
      s = {
        thread: null,
        loaded: false,
        loading: false,
        busy: null,
        error: null,
        errorFor: null,
        unsent: null,
        pending: null,
        since: null,
        hidden: false,
        undo: null,
        asked: null,
      };
      this.shots.set(k, s);
    }
    return s;
  }

  shot(machine: string, shot: string): ShotAiView {
    const s = this.state(machine, shot);
    return {
      thread: s.thread,
      loaded: s.loaded,
      busy: s.busy,
      error: s.error,
      errorFor: s.error ? s.errorFor : null,
      unsent: s.unsent,
      pending: s.pending,
      since: s.since,
      hidden: s.hidden,
      undo: s.undo,
    };
  }

  /** The conversation the cloud keeps for this account and shot (once per page view, after that from the answers). */
  async loadThread(machine: string, shot: string): Promise<void> {
    const s = this.state(machine, shot);
    if (s.loaded || s.loading) return;
    s.loading = true;
    const r = await this.api.thread(machine, shot);
    s.loading = false;
    if (r.ok) {
      s.thread = r.value;
      s.loaded = true;
    } else {
      s.error = r.error.code === 'offline' ? null : r.error.code;
      s.errorFor = 'load';
    }
    this.changed();
  }

  /**
   * A new suggestion. `after`: what has to reach the cloud first (the rating just tapped, which goes into the prompt);
   * the card shows that Claude reads from the tap on. A failed rating asks nothing and says why.
   */
  async suggest(
    machine: string,
    shot: string,
    lang: Lang,
    after?: Promise<{ ok: boolean; error?: { code: string } }>,
  ): Promise<void> {
    const s = this.state(machine, shot);
    if (s.busy) return;
    s.busy = 'suggest';
    s.error = null;
    s.hidden = false;
    s.since = this.now();
    s.undo = null;
    s.asked = { machine, shot, lang, at: s.since };
    this.changed();
    const first = after ? await after : { ok: true };
    const r = first.ok
      ? await this.api.suggest(machine, shot, lang)
      : { ok: false as const, error: { code: first.error?.code ?? 'offline' } };
    s.busy = null;
    s.since = null;
    if (r.ok) {
      s.thread = r.value;
      s.loaded = true;
    } else {
      s.error = r.error.code;
      s.errorFor = 'suggest';
    }
    this.keyGone(r);
    this.changed();
  }

  /**
   * The shot was rated on this device (Dominik 22.10.2026): sour or bitter asks Claude by itself unless the account
   * switched it off. Only the tap asks, never a rating that arrives from another device. True if it asks.
   */
  rated(
    machine: string,
    shot: string,
    rating: number | null,
    lang: Lang,
    saving: Promise<{ ok: boolean; error?: { code: string } }>,
  ): boolean {
    if (!autoAskOn(this.view, rating) || this.state(machine, shot).busy) return false;
    void this.suggest(machine, shot, lang, saving);
    return true;
  }

  /** "Ausblenden" while Claude takes long: the answer still comes and is kept with the shot. */
  hide(machine: string, shot: string): void {
    const s = this.state(machine, shot);
    if (s.hidden) return;
    s.hidden = true;
    this.changed();
  }

  async chat(machine: string, shot: string, text: string, lang: Lang): Promise<void> {
    const s = this.state(machine, shot);
    const t = text.trim().slice(0, 1000);
    if (!t || s.busy) return;
    s.busy = 'chat';
    s.error = null;
    s.unsent = null;
    s.pending = t;
    this.changed();
    const r = await this.api.chat(machine, shot, t, lang);
    s.busy = null;
    s.pending = null;
    if (r.ok) {
      s.thread = r.value;
      s.loaded = true;
    } else {
      s.error = r.error.code;
      s.errorFor = 'chat';
      s.unsent = t;
    }
    this.keyGone(r);
    this.changed();
  }

  /**
   * The proposal of a message was adopted (the machine confirmed): the cloud keeps it as adopted. `changes` (with the
   * values from before) allow "Rückgängig" in this session.
   */
  async markApplied(machine: string, shot: string, index: number, changes?: AiChange[]): Promise<void> {
    const s = this.state(machine, shot);
    s.undo = changes?.length ? { index, changes } : null;
    await this.flag(machine, shot, index, true);
  }

  /** "Rückgängig" set the values from before (the machine confirmed): the proposal is offered again. */
  async markUndone(machine: string, shot: string, index: number): Promise<void> {
    const s = this.state(machine, shot);
    s.undo = null;
    await this.flag(machine, shot, index, false);
  }

  private async flag(machine: string, shot: string, index: number, applied: boolean): Promise<void> {
    const s = this.state(machine, shot);
    // shown at once; the cloud's answer follows
    if (s.thread) {
      const messages = s.thread.messages.map((m, i) => (i === index ? { ...m, applied } : m));
      s.thread = { ...s.thread, messages };
      this.changed();
    }
    const r = await this.api.applied(machine, shot, index, applied);
    if (r.ok) s.thread = r.value;
    this.changed();
  }

  /** The field took the unsent message back. */
  clearUnsent(machine: string, shot: string): void {
    const s = this.state(machine, shot);
    if (s.unsent !== null) {
      s.unsent = null;
      this.changed();
    }
  }

  /**
   * The account has a key (or model) again that fixes these failures (Dominik 23.10.2026: after a new key the card
   * still said "Kein Claude-Schlüssel gespeichert"): they are over. The newest suggestion that failed for them is asked
   * again, since "Einstellungen öffnen" on its card led to the fix; the others clear, a chat message stays in its field.
   */
  private keyBack(codes: ReadonlySet<string>): void {
    let again: ShotState['asked'] = null;
    for (const s of this.shots.values()) {
      if (!s.error || !codes.has(s.error)) continue;
      if (s.errorFor === 'suggest' && s.asked && (!again || s.asked.at > again.at)) again = s.asked;
      s.error = null;
      s.errorFor = null;
    }
    this.changed();
    if (again) void this.suggest(again.machine, again.shot, again.lang);
  }

  /** A stored key Anthropic refuses now or that does not open any more: the account shows it as missing. */
  private keyGone(r: { ok: boolean; error?: { code: string } }): void {
    if (!r.ok && r.error?.code === 'claude_no_key' && this.view.account?.key)
      this.view = { ...this.view, account: { ...this.view.account, key: null } };
  }

  // ---------------------------------------------------------------------------------------------- grinder

  private grinderState(machine: string) {
    let g = this.grinders.get(machine);
    if (!g) {
      g = { data: null, busy: false, error: null, loading: false };
      this.grinders.set(machine, g);
    }
    return g;
  }

  grinder(machine: string): GrinderView {
    const g = this.grinderState(machine);
    return { data: g.data, busy: g.busy, error: g.error };
  }

  /** The machine's grinder and what Claude knows about it; again when the grinder's name changed. */
  async loadGrinder(machine: string, name?: string): Promise<void> {
    const g = this.grinderState(machine);
    if (g.loading || (g.data && (name === undefined || g.data.name === (name || null)))) return;
    g.loading = true;
    const r = await this.api.grinder(machine);
    g.loading = false;
    if (r.ok) g.data = r.value;
    this.changed();
  }

  async lookUpGrinder(machine: string, again: boolean, lang: Lang): Promise<void> {
    const g = this.grinderState(machine);
    if (g.busy) return;
    g.busy = true;
    g.error = null;
    this.changed();
    const r = await this.api.lookUpGrinder(machine, again, lang);
    g.busy = false;
    if (r.ok) g.data = r.value;
    else g.error = r.error.code;
    this.changed();
  }

  // ---------------------------------------------------------------------------------------------- bean

  private beanState(machine: string, bean: string) {
    const k = `${machine}/${bean}`;
    let b = this.beanLookups.get(k);
    if (!b) {
      b = { data: null, busy: false, error: null, loading: false, asked: null };
      this.beanLookups.set(k, b);
    }
    return b;
  }

  beanLookup(machine: string, bean: string): BeanLookupView {
    const b = this.beanState(machine, bean);
    return { data: b.data, busy: b.busy, error: b.error };
  }

  /**
   * What Claude knows about the bean; again when its name or roaster changed (asked: both as one text) or after a
   * suggestion, which looks the bean up by itself once.
   */
  async loadBean(machine: string, bean: string, asked: string, force = false): Promise<void> {
    const b = this.beanState(machine, bean);
    if (b.loading || b.busy || (!force && b.data && b.asked === asked && b.data.status !== 'none')) return;
    b.loading = true;
    const r = await this.api.bean(machine, bean);
    b.loading = false;
    if (r.ok) {
      b.data = r.value;
      b.asked = asked;
    }
    this.changed();
  }

  async lookUpBean(machine: string, bean: string, again: boolean, lang: Lang): Promise<void> {
    const b = this.beanState(machine, bean);
    if (b.busy) return;
    b.busy = true;
    b.error = null;
    this.changed();
    const r = await this.api.lookUpBean(machine, bean, again, lang);
    b.busy = false;
    if (r.ok) b.data = r.value;
    else b.error = r.error.code;
    this.changed();
  }
}
